import type { CareerLessonDetailQuery, CareerLessonsListQuery } from 'core/graphql';

/** `completionKind` is a plain String on the wire (spec 0018 §4). */
import { ICareerExercise, transformCareerExercise } from './transformCareerExercise';

export type CareerCompletionKind = 'acknowledge' | 'artifact' | 'exercise';

export interface ICareerLessonAttachment {
  id: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  /** Signed S3 URL; null when the backend could not sign it. */
  url: string | null;
}

/** One self-check quiz question (spec 0020 Фаза C). */
export interface ICareerQuizQuestion {
  id: string;
  order: number;
  text: string;
  options: string[];
  /**
   * The answer key. ALWAYS `[]` here — the backend hides it on the LMS and only
   * reveals it in the `submitCareerQuiz` result. Kept on the model solely so the
   * shape matches the schema; never read for pre-submit rendering.
   */
  correctIndexes: number[];
}

/**
 * Состояние урока для студента (ТЗ 0071). Считает СЕРВЕР: «открывал, но не
 * прошёл» — это и есть «продолжить», и правило обязано быть одним на главную
 * кнопку и на список. Просмотр страницы урок не завершает.
 */
export type CareerLessonState = 'locked' | 'not_started' | 'in_progress' | 'completed';

export interface ICareerLessonUnlock {
  lessonId: string;
  order: number;
  title: string;
}

const LESSON_STATES: CareerLessonState[] = ['locked', 'not_started', 'in_progress', 'completed'];

/** Неизвестное состояние — «закрыт»: лучше не пустить, чем открыть пустоту. */
const toLessonState = (value: string): CareerLessonState =>
  (LESSON_STATES as string[]).includes(value) ? (value as CareerLessonState) : 'locked';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toUnlock = (row: any): ICareerLessonUnlock | null =>
  row ? { lessonId: row.lessonId, order: row.order, title: row.title } : null;

/** Row shape for the ordered lesson list — teaser fields only. */
export interface ICareerLessonListItem {
  id: string;
  order: number;
  title: string;
  summary: string;
  durationMin: number | null;
  completionKind: CareerCompletionKind;
  /**
   * ТЗ 0069: что у студента ПОЯВИТСЯ после урока — резюме, проект, профиль,
   * отклик или подготовка к интервью. Пустая строка = «не указан», бейдж тогда
   * не рисуется.
   *
   * Оба поля приходят и для ЗАКРЫТОГО урока: список из восьми замков без
   * единого слова о результате читается как ещё один курс.
   */
  outcomeKind: string;
  outcomeLabel: string;
  /**
   * THE gate. When false the lesson is not openable and the server has already
   * withheld `content` / `videoUrl` / `attachments`. Never recomputed client-side.
   */
  isUnlocked: boolean;
  isCompleted: boolean;
  completedAt: string | null;
  /**
   * «Просмотрен» — the student has opened this lesson at least once. Distinct
   * from `isCompleted` and orthogonal to `isUnlocked`: opening never unlocks the
   * next lesson (spec 0020 Фаза B).
   */
  isOpened: boolean;
  /** The ☆/★ bookmark state (spec 0020 Фаза C). */
  isBookmarked: boolean;
  /** ТЗ 0071. */
  state: CareerLessonState;
  /** 'pl' | 'ru' | 'en' — язык МАТЕРИАЛА, не оболочки. */
  materialLanguage: string;
  /** Какой урок откроет закрытый; `null` у открытого. */
  unlockAfter: ICareerLessonUnlock | null;
  /** Состав урока — виден и на закрытом: что внутри, без самого содержимого. */
  hasVideo: boolean;
  attachmentsCount: number;
  quizQuestionsCount: number;
  hasExercise: boolean;
}

export interface ICareerLesson extends ICareerLessonListItem {
  /** Helper text for the artifact link input; empty for 'acknowledge' lessons. */
  artifactHint: string;
  /** First/most recent open timestamp; `null` until the lesson is opened once. */
  openedAt: string | null;
  /** How many times the student has opened the lesson. */
  openCount: number;
  /**
   * Сколько участников прошли этот урок. `null` — не «ноль», а «показывать
   * нечего»: сервер отдаёт число только начиная с порога, потому что
   * «прошёл 1 человек» отговаривает сильнее, чем отсутствие цифры.
   */
  peersCompleted: number | null;
  /** The student's PRIVATE notes; never exposed to staff (spec 0020 §3.3). */
  myNotes: string;
  /** Markdown source. `null` whenever `isUnlocked === false` — server-enforced. */
  content: string | null;
  /** Signed S3 URL. `null` whenever `isUnlocked === false` — server-enforced. */
  videoUrl: string | null;
  /** Always `[]` whenever `isUnlocked === false` — server-enforced. */
  attachments: ICareerLessonAttachment[];
  /** Self-check quiz; `[]` when the lesson has no quiz (spec 0020 Фаза C). */
  quizQuestions: ICareerQuizQuestion[];
  /**
   * ТЗ 0039 — анкета-упражнение. `null`, если анкеты нет ИЛИ урок закрыт:
   * вопросы и рекомендации это тело урока, и сервер их не отдаёт.
   */
  exercise: ICareerExercise | null;
}

const KNOWN_KINDS: CareerCompletionKind[] = ['acknowledge', 'artifact', 'exercise'];

const isKnownKind = (value: string): value is CareerCompletionKind =>
  (KNOWN_KINDS as string[]).includes(value);

/**
 * An unknown `completion_kind` from a newer backend degrades to 'acknowledge':
 * the student can still finish the lesson with a plain confirmation instead of
 * facing a dead button. If that guess is wrong the server rejects the mutation
 * with a customError on `artifactUrl`, which the page surfaces — so the
 * fallback can never silently skip a required artifact.
 */
const toCompletionKind = (value: string): CareerCompletionKind =>
  isKnownKind(value) ? value : 'acknowledge';

/**
 * Normalizes `careerLessons`. Returns `[]` for a non-member — the backend
 * answers with an empty list rather than an error (spec 0018 §4).
 * The list arrives ordered by `order`; sorted again defensively so the numbering
 * shown to the student always matches the unlock chain.
 */
export const transformCareerLessons = (
  data: CareerLessonsListQuery | undefined,
): ICareerLessonListItem[] =>
  [...(data?.careerLessons ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((lesson) => ({
      id: lesson.id,
      order: lesson.order,
      title: lesson.title,
      summary: lesson.summary,
      durationMin: lesson.durationMin ?? null,
      completionKind: toCompletionKind(lesson.completionKind),
      outcomeKind: lesson.outcomeKind,
      outcomeLabel: lesson.outcomeLabel,
      isUnlocked: lesson.isUnlocked,
      isCompleted: lesson.isCompleted,
      completedAt: lesson.completedAt ?? null,
      isOpened: lesson.isOpened,
      isBookmarked: lesson.isBookmarked,
      state: toLessonState(lesson.state),
      materialLanguage: lesson.materialLanguage,
      unlockAfter: toUnlock(lesson.unlockAfter),
      hasVideo: lesson.hasVideo,
      attachmentsCount: lesson.attachmentsCount,
      quizQuestionsCount: lesson.quizQuestionsCount,
      hasExercise: lesson.hasExercise,
    }));

/**
 * Normalizes a single `careerLesson`. `null` in / `null` out — a missing lesson
 * (unpublished, wrong id, or non-member) is rendered as "not found" by the page.
 */
export const transformCareerLesson = (
  data: CareerLessonDetailQuery | undefined,
): ICareerLesson | null => {
  const lesson = data?.careerLesson;

  if (!lesson) {
    return null;
  }

  return {
    id: lesson.id,
    order: lesson.order,
    title: lesson.title,
    summary: lesson.summary,
    durationMin: lesson.durationMin ?? null,
    completionKind: toCompletionKind(lesson.completionKind),
    outcomeKind: lesson.outcomeKind,
    outcomeLabel: lesson.outcomeLabel,
    artifactHint: lesson.artifactHint,
    isUnlocked: lesson.isUnlocked,
    isCompleted: lesson.isCompleted,
    completedAt: lesson.completedAt ?? null,
    isOpened: lesson.isOpened,
    openedAt: lesson.openedAt ?? null,
    openCount: lesson.openCount,
    peersCompleted: lesson.peersCompleted ?? null,
    myNotes: lesson.myNotes ?? '',
    isBookmarked: lesson.isBookmarked,
    state: toLessonState(lesson.state),
    materialLanguage: lesson.materialLanguage,
    unlockAfter: toUnlock(lesson.unlockAfter),
    hasVideo: lesson.hasVideo,
    attachmentsCount: lesson.attachmentsCount,
    quizQuestionsCount: lesson.quizQuestionsCount,
    hasExercise: lesson.hasExercise,
    content: lesson.content ?? null,
    videoUrl: lesson.videoUrl ?? null,
    attachments: (lesson.attachments ?? []).map((attachment) => ({
      id: attachment.id,
      originalName: attachment.originalName,
      contentType: attachment.contentType,
      sizeBytes: attachment.sizeBytes,
      url: attachment.url ?? null,
    })),
    exercise: transformCareerExercise(
      (lesson as { exercise?: Parameters<typeof transformCareerExercise>[0] }).exercise,
    ),
    quizQuestions: [...(lesson.quizQuestions ?? [])]
      .sort((a, b) => a.order - b.order)
      .map((question) => ({
        id: question.id,
        order: question.order,
        text: question.text,
        options: [...question.options],
        // Always `[]` from the LMS resolver; kept for shape parity only.
        correctIndexes: [...question.correctIndexes],
      })),
  };
};

/** Human-readable attachment size for the download link. */
export const formatAttachmentSize = (sizeBytes: number): string => {
  if (sizeBytes <= 0) {
    return '';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const power = Math.min(units.length - 1, Math.floor(Math.log(sizeBytes) / Math.log(1024)));
  const value = sizeBytes / 1024 ** power;
  return `${power === 0 ? value : value.toFixed(1)} ${units[power]}`;
};
